import { Editor } from "@tiptap/core";
import BubbleMenu from "@tiptap/extension-bubble-menu";
import { EditorTheme } from "./theme.js";
import { EXTENSIONS, EDITOR_PROPS, FLOATING_MENUS } from "./config.js";
import { TableOfContents } from "./toc.js";
import { TableManager } from "./table_manager.js";
import { ImageManager } from "./image_manager.js";
import { queueSave } from "./offline.js";
import { getCSRFToken } from "../shared/htmx-helpers.js";

export class EditorController {
  #editor;
  // Assigned in the constructor's try-block (after the Editor is created), so
  // TS can't prove definite assignment — assert it.
  tableManager!: TableManager;
  imageManager!: ImageManager;
  store: any;
  config: any;
  toc: TableOfContents;
  saveTimeout: any;
  currentToCHTML: string;
  // Set by teardown(): a torn-down controller never saves again (neither a
  // pending debounce nor a scheduled retry).
  destroyed = false;
  pendingContent: any = null;

  constructor(element, store, config) {
    this.store = store;
    this.config = config; // { saveUrl, uploadUrl, content, i18n }
    this.toc = new TableOfContents();
    this.saveTimeout = null;
    this.currentToCHTML = "";

    // Dynamic Bubble Menu Registration
    const menuExtensions = FLOATING_MENUS.map((menuConfig) => {
      const menuElement = document.getElementById(menuConfig.id);
      if (!menuElement) {
        console.warn(`Floating menu element #${menuConfig.id} not found.`);
        return null;
      }

      // Create a unique extension to avoid "Duplicate extension names" warning
      const CustomBubbleMenu = BubbleMenu.extend({
        name: `bubbleMenu-${menuConfig.id}`,
      });

      return CustomBubbleMenu.configure({
        pluginKey: `bubbleMenu-${menuConfig.id}`,
        element: menuElement,
        shouldShow: menuConfig.shouldShow,
        tippyOptions: {
          duration: 100,
          animation: "scale",
          appendTo: element.closest("#editor-content"), // Keep within Alpine scope logic
          ...menuConfig.tippyOptions,
        },
      });
    }).filter((ext): ext is NonNullable<typeof ext> => ext !== null);

    try {
      this.#editor = new Editor({
        element: element,
        extensions: [...EXTENSIONS, ...menuExtensions],
        editorProps: {
          ...EDITOR_PROPS,
          handlePaste: (view, event, slice) => {
            const items = Array.from(event.clipboardData?.items || []);
            const imageItem = items.find((item) => item.type.indexOf("image") === 0);

            if (imageItem) {
              event.preventDefault();
              const file = imageItem.getAsFile();
              if (file) {
                this.uploadImage(file);
              }
              return true;
            }
            return false;
          },
        },
        content: config.content,
        onUpdate: ({ editor }) => this.onUpdate(editor),
        onSelectionUpdate: () => this.syncState(),
        onTransaction: () => this.syncState(),
        onCreate: () => this.onCreate(),
      });

      this.tableManager = new TableManager(this.#editor);
      this.imageManager = new ImageManager(this.#editor);
    } catch (err) {
      console.error("Editor failed to initialize:", err);
      this.store.status = "error";
      element.innerHTML = `<div class="alert alert-error p-4">
                <span>Editor failed to load. Please refresh the page.</span>
            </div>`;
    }
  }

  onCreate() {
    setTimeout(() => {
      this.refreshToC();
      this.syncState();
    }, 0);
  }

  onUpdate(editor) {
    this.store.status = "unsaved";
    this.debouncedSave(editor.getJSON());
    this.refreshToC();
    this.syncState();
  }

  refreshToC() {
    if (!this.#editor) return;
    // The editor element is available via this.editor.options.element
    this.toc.scan(this.#editor.options.element);
    const html = this.toc.generateHTML();
    if (html !== this.currentToCHTML) {
      this.currentToCHTML = html;
      this.store.tocHTML = html;
    }
  }

  syncState() {
    if (!this.#editor) return;

    // Sync Marks
    const marks = {
      bold: this.#editor.isActive("bold"),
      italic: this.#editor.isActive("italic"),
      underline: this.#editor.isActive("underline"),
      highlight: this.#editor.isActive("highlight"),
      link: this.#editor.isActive("link"),
      heading1: this.#editor.isActive("heading", { level: 1 }),
      heading2: this.#editor.isActive("heading", { level: 2 }),
      heading3: this.#editor.isActive("heading", { level: 3 }),
      bulletList: this.#editor.isActive("bulletList"),
      orderedList: this.#editor.isActive("orderedList"),
      textAlignLeft: this.#editor.isActive({ textAlign: "left" }),
      textAlignCenter: this.#editor.isActive({ textAlign: "center" }),
      textAlignRight: this.#editor.isActive({ textAlign: "right" }),
      textAlignJustify: this.#editor.isActive({ textAlign: "justify" }),

      // Spacing
      lineHeight:
        this.#editor.getAttributes("paragraph").lineHeight ||
        this.#editor.getAttributes("heading").lineHeight,
      paragraphSpacing:
        this.#editor.getAttributes("paragraph").paragraphSpacing ||
        this.#editor.getAttributes("heading").paragraphSpacing,
      fontSize: this.#editor.getAttributes("textStyle").fontSize,
    };

    // Determine Active Node Contexts
    const activeNodes = {
      table: this.tableManager.getNodeState(),
      image: this.imageManager.getNodeState(),
    };

    // Sync Attributes (Color)
    const attrs = {
      color: this.#editor.getAttributes("textStyle").color || "#000000",
    };

    // Sync Stats
    const stats = {
      words: this.#editor.storage.characterCount.words(),
      chars: this.#editor.storage.characterCount.characters(),
    };

    const history = {
      canUndo: this.#editor.can().undo(),
      canRedo: this.#editor.can().redo(),
    };

    this.store.updateState(marks, attrs, stats, history, activeNodes);
  }

  // --- Actions ---

  debouncedSave(content) {
    clearTimeout(this.saveTimeout);
    this.pendingContent = content;
    this.saveTimeout = setTimeout(() => {
      this.saveTimeout = null;
      this.pendingContent = null;
      this.save(content);
    }, 2000);
  }

  /**
   * Tear down a controller whose editor element was replaced by a boosted body
   * swap (the page is no longer reloaded, so its timers used to outlive it).
   * flush=true saves a still-debounced edit first (navigating to ANOTHER
   * document must not lose the last keystrokes); flush=false drops it — used
   * when the SAME document is re-mounted (e.g. after a version restore), where
   * saving the pre-restore content would silently undo the restore.
   */
  teardown(flush: boolean) {
    const pending = this.saveTimeout ? this.pendingContent : null;
    clearTimeout(this.saveTimeout);
    this.saveTimeout = null;
    this.pendingContent = null;
    if (flush && pending) void this.save(pending);
    this.destroyed = true;
    try {
      this.#editor?.destroy();
    } catch {
      /* already gone with its element */
    }
  }

  async save(content, retries = 3) {
    if (!this.config.saveUrl) return;
    // A flush issued by teardown(true) runs before `destroyed` is set; any
    // later call (a retry, a stray debounce) is dropped.
    if (this.destroyed) return;

    // Offline check (Phase 4)
    if (!navigator.onLine) {
      queueSave(this.config.saveUrl, content);
      this.store.status = "offline";
      return;
    }

    this.store.status = "saving";
    try {
      const response = await fetch(this.config.saveUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCSRFToken(),
        },
        body: JSON.stringify({ content: content }),
      });

      if (response.ok) {
        this.store.status = "saved";
      } else {
        if (retries > 0) {
          // Exponential backoff
          const delay = 2000 * (4 - retries);
          console.warn(`Save failed, retrying in ${delay}ms...`);
          setTimeout(() => this.save(content, retries - 1), delay);
        } else {
          console.error("Save failed after retries");
          this.store.status = "error";
        }
      }
    } catch (err) {
      console.error(err);
      if (retries > 0) {
        const delay = 2000 * (4 - retries);
        console.warn(`Save error, retrying in ${delay}ms...`);
        setTimeout(() => this.save(content, retries - 1), delay);
      } else {
        this.store.status = "error";
      }
    }
  }

  async uploadImage(file) {
    // 1. Get Presigned URL
    try {
      const filename =
        file.name || `pasted-image-${Date.now()}.${file.type.split("/")[1] || "png"}`;
      const getUrlResp = await fetch(this.config.uploadUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCSRFToken(),
        },
        body: JSON.stringify({
          filename: filename,
          content_type: file.type,
          intention: "document_image",
          project_id: "", // Not needed for document_image type as per handler logic (uses object_id)
          object_id: this.config.objectId,
        }),
      });

      if (!getUrlResp.ok) {
        throw new Error("Failed to get upload URL");
      }

      const { upload_url, public_url } = await getUrlResp.json();

      // 2. Upload File to Storage
      const uploadResp = await fetch(upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });

      if (!uploadResp.ok) {
        throw new Error("Failed to upload file to storage");
      }

      // 3. Insert Image
      if (public_url) {
        this.#editor.chain().focus().setImage({ src: public_url }).run();
      }
    } catch (e) {
      console.error(e);
      alert(this.config.i18n.upload_failed || "Upload failed");
    }
  }

  triggerImageUpload() {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = () => {
      if (input.files && input.files.length > 0) {
        this.uploadImage(input.files[0]);
      }
    };
    input.click();
  }

  // --- Formatting proxy methods ---
  toggleBold() {
    this.#editor.chain().focus().toggleBold().run();
  }
  toggleItalic() {
    this.#editor.chain().focus().toggleItalic().run();
  }
  toggleUnderline() {
    this.#editor.chain().focus().toggleUnderline().run();
  }
  toggleHighlight() {
    this.#editor.chain().focus().toggleHighlight().run();
  }

  setTextAlign(align) {
    this.#editor.chain().focus().setTextAlign(align).run();
  }
  toggleHeading(level) {
    this.#editor.chain().focus().toggleHeading({ level }).run();
  }
  toggleBulletList() {
    this.#editor.chain().focus().toggleBulletList().run();
  }
  toggleOrderedList() {
    this.#editor.chain().focus().toggleOrderedList().run();
  }

  toggleReference() {
    /* TODO */
  }

  // --- Spacing ---
  setLineHeight(height) {
    this.#editor.chain().focus().setLineHeight(height).run();
  }
  setParagraphSpacing(spacing) {
    this.#editor.chain().focus().setParagraphSpacing(spacing).run();
  }
  setFontSize(size) {
    this.#editor.chain().focus().setFontSize(size).run();
  }
  unsetFontSize() {
    this.#editor.chain().focus().unsetFontSize().run();
  }

  setColor(color) {
    this.#editor.chain().focus().setColor(color).run();
  }

  // --- Table Proxy Methods ---
  insertTable(rows, cols) {
    this.tableManager.insertTable(rows, cols);
  }
  deleteTable() {
    this.tableManager.deleteTable();
  }
  mergeCells() {
    this.tableManager.mergeCells();
  }
  splitCell() {
    this.tableManager.splitCell();
  }
  addColumnBefore() {
    this.tableManager.addColumnBefore();
  }
  addColumnAfter() {
    this.tableManager.addColumnAfter();
  }
  deleteColumn() {
    this.tableManager.deleteColumn();
  }
  addRowBefore() {
    this.tableManager.addRowBefore();
  }
  addRowAfter() {
    this.tableManager.addRowAfter();
  }
  deleteRow() {
    this.tableManager.deleteRow();
  }

  toggleHeaderRow() {
    this.tableManager.toggleHeaderRow();
  }
  toggleHeaderColumn() {
    this.tableManager.toggleHeaderColumn();
  }
  toggleHeaderCell() {
    this.tableManager.toggleHeaderCell();
  }
  setVerticalAlign(align) {
    this.tableManager.setVerticalAlign(align);
  }

  // Image Proxy
  setImageAlign(align: "left" | "center" | "right" | "full") {
    this.imageManager.setAlignment(align);
  }
  setImageWidth(width: string) {
    this.imageManager.setWidth(width);
  }
  setImageWrap(wrap: boolean) {
    this.imageManager.setWrap(wrap);
  }
  setImageMargin(margin: string) {
    this.imageManager.setMargin(margin);
  }
  deleteImage() {
    this.imageManager.deleteImage();
  }

  insertToC() {
    this.#editor.chain().focus().insertContent({ type: "tableOfContents" }).run();
  }

  undo() {
    this.#editor.chain().focus().undo().run();
  }
  redo() {
    this.#editor.chain().focus().redo().run();
  }

  canUndo() {
    return this.#editor ? this.#editor.can().undo() : false;
  }
  canRedo() {
    return this.#editor ? this.#editor.can().redo() : false;
  }

  // Link Management
  getLinkUrl() {
    return this.#editor.getAttributes("link").href || "";
  }

  setLink(url) {
    if (url === "") {
      this.#editor.chain().focus().extendMarkRange("link").unsetLink().unsetUnderline().run();
    } else {
      // Chain underline and color to ensure menu sync
      const secondaryColor = EditorTheme.secondaryColor || "#C5785C";
      this.#editor
        .chain()
        .focus()
        .extendMarkRange("link")
        .setLink({ href: url })
        .setUnderline()
        .setColor(secondaryColor)
        .run();
    }
  }
}
